import {
	Avatar,
	AvatarGroup,
	Box,
	Button,
	Drawer,
	IconButton,
	Divider,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	useTheme,
	Tooltip,
	styled,
	AppBar,
	Menu,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	Typography,
	Toolbar,
	Checkbox,
	Stack,
} from "@mui/material";

import {
	ChevronLeft,
	CalendarMonth,
	Home,
	People,
	Upload,
	Search,
	Add,
	AddCircle,
	AccountCircle,
	AllInclusive,
	Menu as MenuIcon,
	NavigateBefore,
	NavigateNext,
	Warning,
} from "@mui/icons-material";

import { useState, Fragment, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import FlexBetweenBox from "components/FlexBetweenBox";
import { useSelector, useDispatch } from "react-redux";
import { googleScopes } from "constants/googleAuth";
import { useGoogleLogin } from "@react-oauth/google";

import {
	setLogout,
	setCurrentWorkspaces,
	setCalendarView,
	goToPrev,
	goToNext,
	goToToday,
} from "state/reducers/authReducer";

import { MoteColoredIcon } from "constants/mote";
import format from "date-fns/format";

import {
	formatCalendarDate,
	parseLocation,
} from "pages/CalendarPage/CalendarHelper";
import { handleGoogleLinkResponse } from "utils/googleAuth";
import { PeopleDrawerContent } from "pages/PeoplePage/PeopleDrawerContent";

import { NavContext } from "context/NavContext";
import { useContext } from "react";

// Styled components borrowed from mui.com sample code
const miniDrawerWidth = 70;
const openDrawerWidth = 200;

const openedMixin = (theme) => ({
	width: openDrawerWidth,
	transition: theme.transitions.create("width", {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: "hidden",
});

const closedMixin = (theme) => ({
	transition: theme.transitions.create("width", {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: "hidden",
	width: miniDrawerWidth,
});

const DrawerHeader = styled("div")(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "flex-end",
	padding: theme.spacing(0, 1),
	// necessary for content to be below app bar
	...theme.mixins.toolbar,
}));

const StyledDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: openDrawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
 
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }),
);

/* Styled components borrowed from mui.com sample code */
const StyledAppBar = styled(AppBar, {
	shouldForwardProp: (prop) => prop !== "open" || prop !== "fullwidth",
})(({ theme, open, fullwidth }) => ({
	// if drawer is closed in mini size
	width: `calc(100% - ${miniDrawerWidth}px)`, // based on mini drawer width of spacing(7)
	backgroundColor: "background",
	transition: theme.transitions.create(["width", "margin"], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	// if drawer is open in full size
	...(open && {
		marginLeft: openDrawerWidth, // doesn't seem to change anything
		width: `calc(100% - ${openDrawerWidth}px)`,
		transition: theme.transitions.create(["width", "margin"], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
	}),
	// if drawer is hidden from screen
	...(fullwidth && {
		width: "100%",
		transition: theme.transitions.create(["width", "margin"], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
	}),
}));

/* Google Login Component */
const GoogleLogin = ({ stateChanger, state, integration }) => {
	const token = useSelector((state) => state.auth.token);

	const OnGoogleClick = () => {
		console.log("we get here");
		useGoogleLogin({
			onSuccess: async ({ code }) => {
				const url = `${process.env.REACT_APP_PORT}/google/auth/increment`;
				const response = await fetch(url, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({ code }),
				});

				if (response.status === 201) {
					// if response.status === 201, update state by removing integration from state and set new state using stateChanger
					const newState = state?.filter(
						(integration) => integration.id !== integration.id
					);
					stateChanger(newState);
				}
			},
			flow: "auth-code",
			scope: googleScopes, // space seperated string of scopes
			hint: integration.email, // email of card
		});
	};

	return (
		<MenuItem key={integration.id} onClick={OnGoogleClick}>
			<Box display="flex" alignItems="center" gap="0.5rem">
				{integration.thirdPartyType &&
					integration.thirdPartyType.icon({ width: 24, height: 24 })}
				<Avatar
					src={integration.profilePicturePath}
					sx={{ width: 24, height: 24 }}
				/>
				<Typography variant="secondary">{integration.name}</Typography>
			</Box>
		</MenuItem>
	);
};

/* Header Component */
const Header = ({ fullwidth, drawerOpen, isCalendar }) => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const isAuth = Boolean(useSelector((state) => state.auth.token));
    const token = useSelector((state) => state.auth.token);

	const date = useSelector((state) => state.auth.calendar.date);
	const view = useSelector((state) => state.auth.calendar.view);
	const views = ["month", "week", "day", "agenda"];
	const messages = {
		month: "Month",
		week: "Week",
		day: "Day",
		agenda: "Agenda",
	};

    const LeftSide = () => {
		const landingPageOptions = [
			{
				name: "Product",
				link: "/",
			},
			// {
			// 	name: "Enterprise",
			// 	link: "/enterprise",
			// },
			// {
			// 	name: "Pricing",
			// 	link: "/pricing",
			// },
		];

        return (
            <Box display="flex" gap="3rem" alignItems="center">
                <IconButton disableRipple onClick={() => navigate("/")}>
                    <MoteColoredIcon sx={{ fontSize: 48 }} />
                </IconButton>

                {   !isAuth && (
                    // if not logged in, show landing page options
                        <Box display="flex" gap="2rem" justifyContent="center" alignItems="center">
                            {   landingPageOptions?.map((option) => (
                                    <Button key={option.name} onClick={() => navigate(option.link)} sx={{ textTransform: "none" }}>
                                        <Typography variant="h5" fontWeight="600">
                                            {option.name}
                                        </Typography>
                                    </Button>
                                ))
                            }
                        </Box>
                    )
                }
            </Box>
		);
	};
    const CalendarComponents = () => {
        const currentWorkspaces = useSelector(
			(state) => state.auth.user?.currentWorkspaces
		);
		const [missingScopes, setMissingScopes] = useState([]);
		const [anchorEl, setAnchorEl] = useState(null);

		useEffect(() => {
			const getCalendarAccess = async () => {
				const url = new URL(
					`${process.env.REACT_APP_PORT}/calendar/hasCalendarAccess`
				);
				const response = await fetch(url, {
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
				});

				const data = await response.json();

				if (response.status === 200) {
					const missingScopes = Object.entries(data.missingScopes)?.map(
						([key, value]) => ({
							id: key,
							name: value.name,
							profilePicturePath: value.profilePicturePath,
							thirdPartyType: parseLocation(value.thirdPartyType),
							email: value.email,
							displayName: value.displayName,
						})
					);
					setMissingScopes(missingScopes);
				}
			};

			if (isCalendar) getCalendarAccess();
		}, [isCalendar, currentWorkspaces]);

		// menu items
		const open = Boolean(anchorEl);
		const handleClick = (event) => {
			setAnchorEl(event.currentTarget);
		};
		const handleClose = () => {
			setAnchorEl(null);
		};

        return (
            <Box display="flex" gap="1rem" alignItems="center">
                {/* Today Button */}
                <Button
                    variant="outlined"
                    sx={{ textTransform: "none" }}
                    onClick={() => dispatch(goToToday())}
                >
                    <Typography variant="secondary" fontWeight="600">
                        Today
                    </Typography>
                </Button>

                {/* Prev and Next Buttons */}
                <Box display="flex" alignItems="center">
                    <IconButton disableTouchRipple onClick={() => dispatch(goToPrev())}>
                        <NavigateBefore />
                    </IconButton>
                    <IconButton disableTouchRipple onClick={() => dispatch(goToNext())}>
                        <NavigateNext />
                    </IconButton>
                </Box>

                {/* date in current view with date-fns*/}
                <Typography variant="h3" fontWeight="600" color="primary">
                    {formatCalendarDate(date, view)}
                </Typography>

                {/* MissingScopes Dropdown */}
				{/*missingScopes.length > 0 && (
					<Box display="flex" gap="1rem" alignItems="center">
						<Button
							id="basic-button"
							aria-controls={open ? "basic-menu" : undefined}
							aria-haspopup="true"
							aria-expanded={open ? "true" : undefined}
							onClick={handleClick}
						>
							Authorization
						</Button>
						<Menu
							id="basic-menu"
							anchorEl={anchorEl}
							open={open}
							onClose={handleClose}
							MenuListProps={{
								"aria-labelledby": "basic-button",
							}}
						>
							{missingScopes.map((scope) => (
								<GoogleLogin
									key={scope.id}
									stateChanger={setMissingScopes}
									state={missingScopes}
									integration={scope}
								/>
							))}
						</Menu>
					</Box>
                )*/}

				{/* Calendar View Selector */}
				<Select
					value={view}
					//onChange={handleChange}
					size="small"
                    sx={{
                        ml: "4rem"
                    }}
				>
					{views?.map((name) => (
						<MenuItem
							key={name}
							value={name}
							onClick={() => {
								dispatch(
									setCalendarView({
										view: name,
									})
								);
							}}
						>
							<Typography>{messages[name]}</Typography>
						</MenuItem>
					))}
				</Select>
            </Box>
        );
    }
    const RightSide = () => {
        const currentWorkspaces = useSelector(state => state.auth.user?.currentWorkspaces);
        const [currentWorkspacesUI, setCurrentWorkspacesUI] = useState(currentWorkspaces);

		const allWorkspaces =
			useSelector((state) => state.auth.user?.allWorkspaces) || [];
		const allWorkspaceKeys = Object.keys(allWorkspaces);
		const allWorkspaceValues = Object.values(allWorkspaces);

		const token = useSelector((state) => state.auth.token);
		const handleGoogleClick = useGoogleLogin({
			onSuccess: ({ code }) =>
				handleGoogleLinkResponse({ code, token, dispatch }),
			flow: "auth-code",
			scope: googleScopes, // space seperated string of scopes
		});

		// handles changing workspaces for the user on form entry
		const handleWorkspaceChange = async (event) => {
			let updatedWorkspaces = event.target.value;

			// handle special cases
			switch (event.target.value.at(-1)) {
				case "add-workspace":
					handleGoogleClick();
					return;
				case "all-workspaces":
					updatedWorkspaces = allWorkspaceKeys;
					break;
				default:
					break;
			}

			// TODO: check if updatedWorkspaces is the same as currentWorkspaces, if so, do nothing
			if (updatedWorkspaces.length === 0) {
				console.log("Need to have at least one workspace selected");
				return;
			}

			// tentatively set currentWorkspaces to updatedWorkspaces for immediate visual feedback
			/*dispatch(
                setCurrentWorkspaces({
                    currentWorkspaces: updatedWorkspaces,
                }) 
            );*/
			setCurrentWorkspacesUI(updatedWorkspaces);

			// patch request to change current workspace
			const response = await fetch(
				`${process.env.REACT_APP_PORT}/user/updateCurrentWorkspaces`,
				{
					method: "PATCH",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						currentWorkspaces: updatedWorkspaces,
					}),
				}
			);
			//const data = await response.json();

			// TODO: change workspace in redux store and fetch new data to populate dashboard
			if (response.status === 200) {
				dispatch(
					setCurrentWorkspaces({
						currentWorkspaces: updatedWorkspaces,
					})
				);
				setCurrentWorkspacesUI(updatedWorkspaces);
			} else {
				console.log("error");
			}
		};

		const workspaceOptions = allWorkspaceValues?.map((workspace) => (
			<MenuItem
				key={workspace.thirdPartyId}
				value={workspace.thirdPartyId}
				disableTouchRipple
			>
				<Checkbox
					checked={currentWorkspacesUI.includes(workspace.thirdPartyId)}
				/>
				<Box display="flex" alignItems="center" gap="0.5rem">
					<Avatar
						src={workspace.profilePicturePath}
						sx={{ width: 24, height: 24 }}
					/>
					<ListItemText primary={workspace.email} />
				</Box>
			</MenuItem>
		));

        return (
            <Fragment>
                { /* show Account menu component if logged in */
                    isAuth ?  
                        !currentWorkspacesUI ? (
                            // if logged in but no workspaces, allow option to add new workspace button
                                <Button variant="contained" startIcon={<Add/>} sx={{ textTransform: "none" }}>
                                    <Typography variant="body" fontWeight="600">New Workspace</Typography>
                                </Button>
                            ) : (
                                // if logged in and has workspaces, show select menu to change current workspace
                                <Box display="flex" gap="0.5rem" justifyContent="center" alignItems="center">
                                    <FormControl size="small" sx={{ width: 225 }}>
                                        <Select
                                            value={currentWorkspacesUI}
                                            onChange={handleWorkspaceChange}
                                            multiple
                                            MenuProps={{
                                                anchorOrigin: {
                                                    vertical: "bottom",
                                                    horizontal: "right"
                                                },
                                                transformOrigin: {
                                                    vertical: "top",
                                                    horizontal: "right"
                                                },
                                                //getContentAnchorEl: null
                                            }}
                                            renderValue={(selected) => (
                                                // if only one workspace is selected, show its profile picture and the full name
                                                selected.length === 1 ? (
                                                    <Box display="flex" alignItems="center" gap="0.5rem">
                                                        <Avatar 
                                                            src={allWorkspaces[selected[0]]?.profilePicturePath} 
                                                            sx={{ width: 24, height: 24 }} 
                                                        />
                                                        <Typography 
                                                            variant="secondary"
                                                            whiteSpace="nowrap"
                                                            overflow="hidden"
                                                            textOverflow="ellipsis"
                                                        >
                                                            {allWorkspaces[selected[0]]?.email}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                // if more than one workspace is selected, show the profile pictures of the first three workspaces
                                                    <Box display="flex" alignItems="center" gap="0.5rem">
                                                        <AvatarGroup max={3}>
                                                            {   selected?.map((workspaceId) => (
                                                                    <Avatar 
                                                                        key={workspaceId} 
                                                                        src={allWorkspaces[workspaceId]?.profilePicturePath} 
                                                                        sx={{ width: 24, height: 24 }} 
                                                                    />
                                                                ))
                                                            }
                                                        </AvatarGroup>
                                                        <Typography variant="secondary">{selected.length} Workspaces</Typography>
                                                    </Box>
                                                )
                                            )}
                                        >
                                            {   // if more than one workspace, show option to select all workspaces at once
                                                allWorkspaceValues.length > 1 &&
                                                    [
                                                        <MenuItem 
                                                            key="all-workspaces" 
                                                            value="all-workspaces" 
                                                            disableTouchRipple
                                                            disabled={currentWorkspacesUI.length === allWorkspaceValues.length}
                                                        >
                                                            <Box display="flex" alignItems="center" gap="0.5rem">
                                                                <AllInclusive />
                                                                <Typography variant="secondary">All Workspaces</Typography>
                                                            </Box>
                                                        </MenuItem>
                                                        ,
                                                        <Divider key="all-divider" />
                                                    ]
                                            }

												{
													// show all workspaces
													workspaceOptions
												}

                                            { /* add new workspace option at bottom */ }
                                            <Divider />
                                            <MenuItem key="add-workspace" value="add-workspace" disableTouchRipple>
                                                <Box display="flex" alignItems="center" gap="0.5rem">
                                                    <Add />
                                                    <Typography variant="secondary">Add Workspace</Typography>
                                                </Box>
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                        )
                    : (
                    /* show Sign In/Sign Up buttons if not logged in */
                    <Box display="flex" gap="0.5rem">
                        <Button
                            variant="outlined"
                            color="primary"
                            size="large"
                            onClick={() => navigate('/login')}
                        >
                            Sign In
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={() => navigate('/login', { state: { initialPageType: "register" } })}
                        >
                            Sign Up
                        </Button>
                    </Box>
                )}
            </Fragment>
        );
    };

	return (
		<StyledAppBar
			position="fixed"
			elevation={1}
			open={drawerOpen}
			fullwidth={fullwidth}
            sx={{
                borderBottom: 1,
            }}
		>
			<Toolbar>
				<FlexBetweenBox width="100%" zIndex="tooltip">
					<LeftSide />
                    {isCalendar && <CalendarComponents /> }
					<RightSide />
				</FlexBetweenBox>
			</Toolbar>
		</StyledAppBar>
	);
};

/* Sidebar Component */
const SideBar = ({ drawerOpen, onDrawerOpen, onDrawerClose }) => {
    const location = useLocation();
    const pathname = `/${location.pathname.split('/')[1]}`;

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();

    // List of sidebar options
    const listOptions = [
        {
            name: "Home",
            icon: <Home />,
            pathname: "/",
        },
        {
            name: "Calendar",
            icon: <CalendarMonth />,
            pathname: "/calendar",
        },
        {
            name: "People",
            icon: <People />,
            pathname: "/people",
        },
        {
            name: "Upload",
            icon: <Upload />,
            pathname: "/upload",
        },
        {
            name: "Search",
            icon: <Search />,
            pathname: "/search",
        },
        // {
        //     name: "Integrate",
        //     icon: <AddCircle />,
        //     pathname: "/integrations",
        // },
    ];

	/* Account menu component helpers*/
	const [anchorEl, setAnchorEl] = useState(null);
	const accountMenuOpen = Boolean(anchorEl);

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};

	const profilePicturePath = useSelector(
		(state) => state.auth.user?.profilePicturePath
	);
	const firstName = useSelector((state) => state.auth.user?.firstName);
	const lastName = useSelector((state) => state.auth.user?.lastName);
	const username = `${firstName} ${lastName}`

	return (
		<StyledDrawer variant="permanent" open={drawerOpen} >
			{/* Sidebar open/close icon */}
			{/*<DrawerHeader>
				<IconButton onClick={drawerOpen ? onDrawerClose : onDrawerOpen}>
					{drawerOpen ? <ChevronLeft /> : <MenuIcon />}
				</IconButton>
            </DrawerHeader>*/}
            <Toolbar/>
			{<Divider />}

            {/* Sidebar options */}
            <List>
                {listOptions?.map((option) => (
                    <ListItem key={option.name} disablePadding sx={{ display: 'block' }}>
                            <ListItemButton
                                LinkComponent={Link}
                                to={option.pathname}
                                disableRipple
                                //onClick={() => navigate(option.pathname)}
                                sx={{
                                    justifyContent: 'center', alignItems: 'center',
                                    minHeight: 56,
                                    //justifyContent: drawerOpen ? 'initial' : 'center',
                                    px: 2.5,
                                    backgroundColor: pathname === option.pathname ? 'primary.main' : 'transparent',
                                    '&:hover': {
                                        backgroundColor:  pathname === option.pathname ? 'primary.main' : null,
                                    },
                                }}
                            >   
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                }}>
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            //mr: drawerOpen ? 3 : 'auto',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            color: pathname === option.pathname ? 'on_primary.main' : null,
                                        }}
                                    >
                                        {option.icon}
                                    </ListItemIcon>
                                    <Typography fontSize="12px" fontWeight="400" sx={{ color: pathname === option.pathname ? 'on_primary.main' : null }}>
                                        {option.name}
                                    </Typography>
                                {/*<ListItemText 
                                    primary={option.name} 
                                    primaryTypographyProps={{ variant: 'h5', fontWeight: '500' }} 
                                    sx={{ 
                                        opacity: drawerOpen ? 1 : 0,
                                        color: pathname === option.pathname ? 'on_primary.main' : null,
                                    }} 
                                />*/}
                                </Box>
                            </ListItemButton>
                    </ListItem>
                ))}
            </List>
            
            <Box mt="auto" mb="1rem" display="flex" justifyContent="center">
                <IconButton onClick={handleClick} disableRipple>
                    <Avatar src={profilePicturePath}/>
                </IconButton>
            </Box>
            
            <Menu
                anchorEl={anchorEl}
                open={accountMenuOpen}
                onClose={handleClose}
                onClick={handleClose}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                sx={{ zIndex: theme.zIndex.tooltip + 1}}
            >
                <MenuItem disabled>Signed in as <Typography fontWeight="700">&nbsp;{`${username}`}</Typography></MenuItem>
                {/* <Divider/>
                <MenuItem>Your Profile</MenuItem>
                <MenuItem>Your Notes</MenuItem>
                <MenuItem>Settings</MenuItem>
                <Divider /> */}
                <MenuItem 
                    onClick={() => { dispatch(setLogout()); navigate('/'); }}
                >
                    Sign out
                </MenuItem>
            </Menu>
        </StyledDrawer>
    );
};

/* Main component */
const NavDrawer = ({ showHeader, showSidebar, showSecondarySidebar, isCalendar }) => {
    const location = useLocation();
    const isAuth = Boolean(useSelector(state => state.auth.token));

    //const { navState, setNavState } = useContext(NavContext);
    
    const handleLocationChange = useCallback(() => {
        const basepath = location.pathname.split('/')[1];
        const nextNavState = {
            showHeader: true,
            showSidebar: true,
            showSecondarySidebar: false,
            isCalendar: false,
            isPeople: false,
        };
        console.log(basepath)
        switch (basepath) {
            // custom options
            case "":
                nextNavState.showSidebar = isAuth;
                break;
            case "calendar":
                nextNavState.showSecondarySidebar = true;
                nextNavState.isCalendar = true;
                break;
            case "people":
                nextNavState.showSecondarySidebar = true;
                nextNavState.isPeople = true;
                break;
            // use default options
            case "upload":
            case "search":
            case "integrations":
                break;
            // turn off header and sidebar for login and register pages
            case "login":
            case "register":
            default:    // 404 page
                nextNavState.showHeader = false;
                nextNavState.showSidebar = false;
                break;
        }
        
        return nextNavState;
    }, [location]);
    
    const [navState, setNavState] = useState(() => handleLocationChange(location));

    useEffect(() => {
        const nextNavState = handleLocationChange();
        setNavState(nextNavState);
    }, [location]);

	/* Drawer open state transparant to header and sidebar */
	const [drawerOpen, setDrawerOpen] = useState(false);

	const handleDrawerOpen = () => {
		setDrawerOpen(true);
	};

	const handleDrawerClose = () => {
		setDrawerOpen(false);
	};

	return (
		<Fragment>
			{navState.showHeader && (
				<Header
					fullwidth={navState.showHeader && !navState.showSidebar ? "true" : undefined}
					drawerOpen={drawerOpen}
					isCalendar={navState.isCalendar}
				/>
			)}
			{navState.showSidebar && (
				<SideBar
					drawerOpen={drawerOpen}
					onDrawerOpen={handleDrawerOpen}
					onDrawerClose={handleDrawerClose}
				/>
			)}
            {navState.isPeople && (
                <Drawer
                    open
                    variant="permanent"
                    sx={{
                        overflowX: 'hidden',
                        width: 270,
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        boxSizing: 'border-box',
                        '& .MuiDrawer-paper': {
                            overflowX: 'hidden',
                            width: 270,
                            marginX: '70px',
                            marginTop: '64px',
                        },
                        zIndex: (theme) => theme.zIndex.appBar - 1,
                    }}
                >
                    <PeopleDrawerContent />
                </Drawer>
            )}
		</Fragment>
	);
};

export default NavDrawer;
